import { Box, SxProps } from '@mui/material';
import { Button } from 'system/atoms';
import { BackButton } from 'components/Buttons';
import { Header } from 'components/Header';
import { SearchInput } from 'components/Inputs/SearchInput';
import { TextLoading } from 'components/Loading';
import { TableV2, TableV2Column } from 'components/Table/TableV2';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePaginatedQuery, useQuery, useSubscriber } from 'services/api/hooks';
import {
  getCandidateBatchGroup,
  searchCandidateBatches
} from 'services/candidate/api';
import {
  CandidateBatch,
  CandidateBatchGroup,
  CandidateBatchStatus
} from 'services/candidate/types';
import { Paths } from 'services/navigation/paths';
import { formatToDate, formatToDateTime } from 'utils/datetime';
import TuneIcon from '@mui/icons-material/Tune';
import { AnchorDisplayText } from 'components/Text';
import { useDrawer } from 'services/drawer/hooks';
import { DrawerComponentType } from 'services/drawer/types';
import {
  onCreateCandidateBatch,
  onUpdateCandidateBatch
} from 'services/candidate/subscriptions';
import { sanitizeObject } from 'utils/fomatting';
import { useDebounce } from '@uidotdev/usehooks';
import { sortFields } from './constants';
import { useTracking } from 'services/tracking/hooks';
import { TrackingEventName } from 'services/tracking/constants';
import { RouterLink } from 'common/ui/RouterLink';
import { getAnalysisTitle } from 'utils/getAnalysisTitle';

const container: SxProps = {
  p: 3
};

const subHeaderContainer: SxProps = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  mb: 2
};

const columns: TableV2Column<CandidateBatch>[] = [
  {
    name: 'Name',
    cell: (row) => {
      const content = (() => {
        if (
          row.status === CandidateBatchStatus.PRE_PROCESSING ||
          row.status === CandidateBatchStatus.READY_TO_PROCESS ||
          row.status === CandidateBatchStatus.CREATED
        ) {
          return <TextLoading text="Preparing analysis" />;
        }

        return getAnalysisTitle(row, row.id);
      })();

      return (
        <RouterLink to={`${Paths.CANDIDATES_RANKING}/${row.id}`}>
          <AnchorDisplayText>{content}</AnchorDisplayText>
        </RouterLink>
      );
    }
  },
  {
    name: 'Location',
    cell: (row) => row.jobDescriptionParsed?.location || '-'
  },
  {
    name: 'Candidates',
    selector: (row: CandidateBatch) => row.candidatesCount,
    sortable: true,
    sortField: sortFields.candidatesCount,
    id: sortFields.candidatesCount
  },
  {
    name: 'Last activity',
    selector: (row) => formatToDateTime(row.updatedAt),
    sortable: true,
    sortField: sortFields.updatedAt,
    id: sortFields.updatedAt
  },

  {
    name: 'Job reference',
    cell: (row: CandidateBatch) => row.atsJobReference || '-'
  },
  {
    name: 'Job ID',
    selector: (row) => row.atsJobId || '-'
  },
  {
    name: 'Created by',
    selector: (row) => row.user?.name || '-'
  }
];

export const CandidateRankingGroupPage = () => {
  const { id } = useParams();
  const [searchValue, setSearchValue] = useState('');
  const [sort, setSort] = useState({
    field: sortFields.updatedAt,
    direction: 'desc'
  });
  const [limit, setLimit] = useState(50);
  const [candidateBatchesList, setCandidateBatchesList] = useState<
    CandidateBatch[]
  >([]);
  const navigate = useNavigate();
  const { openDrawer } = useDrawer();
  const { track } = useTracking();

  const { data: candidateBatchGroup } = useQuery<CandidateBatchGroup>(
    getCandidateBatchGroup,
    {
      id
    }
  );

  const debouncedSearchValue = useDebounce(searchValue, 500);

  const { handleNext, handlePrevious, totalItems, isLoading, refetch } =
    usePaginatedQuery<CandidateBatch>({
      queryFn: searchCandidateBatches,
      limit,
      variables: {
        search: debouncedSearchValue,
        sort,
        candidateBatchGroupId: id
      },
      onFetchSuccess: (data) => {
        setCandidateBatchesList(data.items);
      }
    });

  useSubscriber({
    subQuery: onCreateCandidateBatch,
    subParams: {
      candidateBatchGroupId: { eq: id }
    },
    onFetchSuccess: () => {
      refetch();
    }
  });

  useSubscriber({
    subQuery: onUpdateCandidateBatch,
    subParams: {
      candidateBatchGroupId: { eq: id }
    },
    onFetchSuccess: (data) => {
      const candidateBatch: CandidateBatch = data.onUpdateCandidateBatch;
      const index = candidateBatchesList.findIndex(
        (x) => x.id === candidateBatch.id
      );
      if (index !== -1) {
        const newItems = [...candidateBatchesList];
        const item = newItems[index];
        newItems[index] = {
          ...item,
          ...sanitizeObject(candidateBatch)
        };
        setCandidateBatchesList(newItems);
        return;
      }
      refetch();
    }
  });

  if (!candidateBatchGroup) {
    return null;
  }

  const addJobs = () => {
    track(TrackingEventName.RulesetAddJob);

    navigate(Paths.ANALYSE_RULESET_ADD_JOBS, {
      state: {
        candidateBatchGroupId: id
      }
    });
  };

  const handleSort = (
    column: TableV2Column<CandidateBatch>,
    sortDirection: 'asc' | 'desc'
  ) => {
    const { sortField } = column;

    if (!sortField) return;

    setSort({
      field: sortField,
      direction: sortDirection
    });
  };

  const handleRowsPerPageChange = async (newPerPage: number) => {
    setLimit(newPerPage);
  };

  return (
    <Box sx={container}>
      <Header
        title={candidateBatchGroup.name}
        subtitle={`Last modified ${formatToDate(
          candidateBatchGroup.updatedAt
        )}`}
        prevPagePath={Paths.CANDIDATES_RANKING}
        prevPageTitle="Analysis"
        currentPageTitle={candidateBatchGroup.name}
      />
      <BackButton containerStyle={{ mb: 2 }} />

      <Box sx={subHeaderContainer}>
        <Box>
          <SearchInput
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Search role"
            onClear={() => {
              setSearchValue('');
            }}
            isLoading={isLoading}
          />
        </Box>

        <Box display="flex" gap={2}>
          <Button
            startIcon={<TuneIcon />}
            onClick={() => {
              openDrawer({
                drawerContentType:
                  DrawerComponentType.CandidateRankingGroupEdit,
                drawerComponentProps: {
                  candidateBatchGroup,
                  hideCloseIconButton: true
                }
              });
            }}
          >
            Edit Ruleset
          </Button>
          <Button variant="primary" onClick={addJobs}>
            Add jobs
          </Button>
        </Box>
      </Box>
      <TableV2
        data={candidateBatchesList}
        columns={columns}
        pagination
        sortServer
        paginationServer
        sortField={sort.field}
        onSort={handleSort}
        isLoading={isLoading}
        handleNext={handleNext}
        handlePrevious={handlePrevious}
        paginationTotalRows={totalItems}
        onChangeRowsPerPage={handleRowsPerPageChange}
      />
    </Box>
  );
};
